// A schedule's exact leg mix (`filters.leg_mix`): how many of every pack's
// legs come from each market and period, and which markets share a player.
// Data only, with no imports, so the recipe model can clean a mix without an
// import cycle; prepackLegMixLabels.js words it.

/** The most legs a pack takes (the server's rules.BetTypeLegRange). */
export const MAX_MIX_LEGS = 6

const token = (value) => String(value ?? '').trim().toLocaleLowerCase()

/**
 * `filters.leg_mix` as the server stores it: `[{ market_key, period, count,
 * same_player_as?, same_team_as? }]`, keys and periods trimmed and lower-cased, counts whole
 * and at least 1. A row without a period keeps '': the server reads it as its
 * market's only pick, which only the schedule's picks can tell (the builder's
 * legMix.js resolves it). A row repeating a market and period, and a player
 * or team link to its own key or to a key the mix does not hold, are dropped.
 * [] for anything that is not a list.
 */
export function cleanLegMix(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const rows = []
  for (const row of value) {
    const key = token(row?.market_key)
    const period = token(row?.period)
    const count = Math.trunc(Number(row?.count))
    if (!key || !(count >= 1)) continue
    const id = `${key}|${period}`
    if (seen.has(id)) continue
    seen.add(id)
    rows.push({ market_key: key, period, count, same_player_as: token(row?.same_player_as), same_team_as: token(row?.same_team_as) })
  }
  const keys = new Set(rows.map((row) => row.market_key))
  const kept = (link, row) => Boolean(link) && link !== row.market_key && keys.has(link)
  return rows.map(({ same_player_as: player, same_team_as: team, ...row }) => ({
    ...row,
    ...(kept(player, row) ? { same_player_as: player } : {}),
    ...(kept(team, row) ? { same_team_as: team } : {}),
  }))
}

/** How many legs a mix makes: the sum of its counts (0 without one). */
export function legMixTotal(mix) {
  return cleanLegMix(mix).reduce((total, row) => total + row.count, 0)
}
